import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Worker, Activity, Alert, WebSocketStatus, Location } from '../../types';
import { normalizeAlertSeverity } from '../../utils/alertSeverity';

/**
 * Record of the last REST seed of open alerts (GET /facilities/:id/alerts?status=active).
 * `total` is the server's filter-respecting count, independent of the 100-row cap on
 * `alerts`; `ids` are the rows that seed returned so consumers can tell which in-memory
 * alerts were open at `fetchedAt` and adjust `total` by live acknowledgements/resolutions.
 */
export interface OpenAlertSeed {
  facilityId: string;
  ids: string[];
  total: number;
  /** True when the seed returned every open alert (fewer rows than the request limit, or as many as the server total). */
  complete: boolean;
  fetchedAt: number;
}

/** `ids` is derived by the reducer from `alerts`, so callers supply everything else. */
export type SeedAlertsPayload = Omit<OpenAlertSeed, 'ids'> & {
  alerts: Alert[];
};

interface RealtimeState {
  workers: Record<string, Worker>;
  activities: Record<string, Activity>;
  alerts: Alert[];
  /** Last REST seed of open alerts for the active facility; null until the shell loads one. */
  openAlertSeed: OpenAlertSeed | null;
  connectionStatus: WebSocketStatus;
  lastUpdate: number;
  activeZones: Record<string, number>; // zone_id -> worker count
}

const initialState: RealtimeState = {
  workers: {},
  activities: {},
  alerts: [],
  openAlertSeed: null,
  connectionStatus: {
    connected: false,
    connectionStatus: 'disconnected',
  },
  lastUpdate: Date.now(),
  activeZones: {},
};

function alertWorkerId(alert: Alert): string | undefined {
  return alert.worker_id || alert.user_id || alert.person_id;
}

function workerAlertStatus(
  alerts: Alert[],
  workerId: string,
): 'normal' | 'warning' | 'critical' {
  let status: 'normal' | 'warning' | 'critical' = 'normal';

  for (const alert of alerts) {
    if (alertWorkerId(alert) !== workerId || alert.acknowledged || alert.resolved) {
      continue;
    }
    const severity = normalizeAlertSeverity(alert.severity);
    if (severity === 'critical') return 'critical';
    if (severity === 'high') status = 'warning';
  }

  return status;
}

const realtimeSlice = createSlice({
  name: 'realtime',
  initialState,
  reducers: {
    updateWorkerLocation: (state, action: PayloadAction<{
      workerId: string;  // Use id instead of person_id
      location: Location;
      current_zone?: string;
      current_activity?: string;
    }>) => {
      const { workerId, location, current_zone, current_activity } = action.payload;
      
      if (state.workers[workerId]) {
        // Update existing worker
        const previousZone = state.workers[workerId].current_zone;
        
        state.workers[workerId] = {
          ...state.workers[workerId],
          location,
          current_zone,
          current_activity,
          lastUpdate: Date.now(),
        };
        
        // Update zone counts
        if (previousZone && previousZone !== current_zone) {
          state.activeZones[previousZone] = Math.max(0, (state.activeZones[previousZone] || 0) - 1);
        }
        if (current_zone) {
          state.activeZones[current_zone] = (state.activeZones[current_zone] || 0) + 1;
        }
      }
      
      state.lastUpdate = Date.now();
    },
    
    updateWorkerStatus: (state, action: PayloadAction<{
      workerId: string;  // Use id instead of person_id
      alert_status?: 'normal' | 'warning' | 'critical';
      sensor_status?: 'active' | 'inactive' | 'disconnected';
    }>) => {
      const { workerId, alert_status, sensor_status } = action.payload;
      
      if (state.workers[workerId]) {
        if (alert_status) {
          state.workers[workerId].alert_status = alert_status;
        }
        if (sensor_status) {
          state.workers[workerId].sensor_status = sensor_status;
        }
        state.workers[workerId].lastUpdate = Date.now();
      }
      
      state.lastUpdate = Date.now();
    },
    
    addWorker: (state, action: PayloadAction<Worker>) => {
      const worker = action.payload;
      state.workers[worker.id] = worker;  // Use id instead of person_id
      
      if (worker.current_zone) {
        state.activeZones[worker.current_zone] = (state.activeZones[worker.current_zone] || 0) + 1;
      }
      
      state.lastUpdate = Date.now();
    },
    
    removeWorker: (state, action: PayloadAction<string>) => {
      const workerId = action.payload;
      const worker = state.workers[workerId];
      
      if (worker) {
        if (worker.current_zone) {
          state.activeZones[worker.current_zone] = Math.max(0, (state.activeZones[worker.current_zone] || 0) - 1);
        }
        delete state.workers[workerId];
      }
      
      state.lastUpdate = Date.now();
    },
    
    addActivity: (state, action: PayloadAction<Activity>) => {
      const activity = action.payload;
      state.activities[activity.id] = activity;
      
      // Update worker's current activity (use user_id or fallback to person_id)
      const workerId = activity.user_id || activity.person_id;
      if (workerId && state.workers[workerId]) {
        state.workers[workerId].current_activity = activity.activity_type;
      }
      
      state.lastUpdate = Date.now();
    },
    
    updateActivity: (state, action: PayloadAction<Activity>) => {
      const activity = action.payload;
      if (state.activities[activity.id]) {
        state.activities[activity.id] = activity;
      }
      state.lastUpdate = Date.now();
    },
    
    addAlert: (state, action: PayloadAction<Alert>) => {
      const alert = action.payload;

      // Dedupe by id: a reconnect warm-start or a replayed event for an alert we
      // already hold must update it in place, not add a second copy (F2-2).
      const existingIndex = state.alerts.findIndex((existing) => existing.id === alert.id);
      if (existingIndex !== -1) {
        state.alerts[existingIndex] = { ...state.alerts[existingIndex], ...alert };
      } else {
        // Add to beginning of array (most recent first)
        state.alerts.unshift(alert);

        // Keep only last 100 alerts in memory
        if (state.alerts.length > 100) {
          state.alerts = state.alerts.slice(0, 100);
        }
      }
      
      // Prefer the worker row ID; older payloads used user_id or person_id.
      const workerId = alertWorkerId(alert);
      if (workerId && state.workers[workerId]) {
        state.workers[workerId].alert_status = workerAlertStatus(state.alerts, workerId);
      }
      
      state.lastUpdate = Date.now();
    },
    
    /**
     * Seeds the in-memory alert list from a REST snapshot (GET /facilities/:id/alerts)
     * so the Dashboard shows open alerts on first paint instead of only those that
     * arrive over the stream afterwards (F2-1). Live entries already present win;
     * only unseen ids are added. Result stays most-recent-first and capped at 100.
     */
    seedAlerts: (state, action: PayloadAction<SeedAlertsPayload>) => {
      const { alerts: incoming, ...seed } = action.payload;
      const known = new Set(state.alerts.map((existing) => existing.id));
      const additions = incoming.filter((alert) => Boolean(alert?.id) && !known.has(alert.id));

      if (additions.length > 0) {
        const merged = [...state.alerts, ...additions].sort(
          (a, b) => (Number(b.timestamp) || 0) - (Number(a.timestamp) || 0),
        );
        state.alerts = merged.slice(0, 100);

        // Only rows that name a worker ROW id may touch worker status. REST rows carry
        // person_id (a user id); resolving that through alertWorkerId's fallback could
        // write another worker's alert_status when ids collide.
        const touched = new Set<string>();
        for (const alert of additions) {
          if (alert.worker_id) touched.add(alert.worker_id);
        }
        for (const workerId of touched) {
          if (state.workers[workerId]) {
            state.workers[workerId].alert_status = workerAlertStatus(state.alerts, workerId);
          }
        }
      }

      state.openAlertSeed = { ...seed, ids: incoming.map((alert) => alert.id) };
      state.lastUpdate = Date.now();
    },

    acknowledgeAlert: (state, action: PayloadAction<{ alertId: string; acknowledgedBy: string }>) => {
      const { alertId, acknowledgedBy } = action.payload;
      const alert = state.alerts.find(a => a.id === alertId);
      
      if (alert) {
        alert.acknowledged = true;
        alert.acknowledged_by = acknowledgedBy;
        alert.acknowledged_at = Date.now();

        const workerId = alertWorkerId(alert);
        if (workerId && state.workers[workerId]) {
          state.workers[workerId].alert_status = workerAlertStatus(state.alerts, workerId);
        }
      }
      
      state.lastUpdate = Date.now();
    },

    setAlertAcknowledgement: (state, action: PayloadAction<{
      alertId: string;
      acknowledged: boolean;
      acknowledgedBy?: string;
      acknowledgedAt?: number;
      expected?: {
        acknowledged: boolean;
        acknowledgedBy?: string;
        acknowledgedAt?: number;
      };
    }>) => {
      const {
        alertId,
        acknowledged,
        acknowledgedBy,
        acknowledgedAt,
        expected,
      } = action.payload;
      const alert = state.alerts.find(a => a.id === alertId);

      if (alert) {
        if (
          expected
          && (
            alert.acknowledged !== expected.acknowledged
            || alert.acknowledged_by !== expected.acknowledgedBy
            || alert.acknowledged_at !== expected.acknowledgedAt
          )
        ) return;

        alert.acknowledged = acknowledged;
        if (acknowledgedBy === undefined) {
          delete alert.acknowledged_by;
        } else {
          alert.acknowledged_by = acknowledgedBy;
        }
        if (acknowledgedAt === undefined) {
          delete alert.acknowledged_at;
        } else {
          alert.acknowledged_at = acknowledgedAt;
        }

        const workerId = alertWorkerId(alert);
        if (workerId && state.workers[workerId]) {
          state.workers[workerId].alert_status = workerAlertStatus(state.alerts, workerId);
        }
      }

      state.lastUpdate = Date.now();
    },

    setAlertResolution: (state, action: PayloadAction<{
      alertId: string;
      resolved: boolean;
      resolvedAt?: number;
      acknowledged: boolean;
      acknowledgedBy?: string;
      acknowledgedAt?: number;
    }>) => {
      const {
        alertId,
        resolved,
        resolvedAt,
        acknowledged,
        acknowledgedBy,
        acknowledgedAt,
      } = action.payload;
      const alert = state.alerts.find(a => a.id === alertId);

      if (alert) {
        alert.resolved = resolved;
        if (resolvedAt === undefined) {
          delete alert.resolved_at;
        } else {
          alert.resolved_at = resolvedAt;
        }
        alert.acknowledged = acknowledged;
        if (acknowledgedBy === undefined) {
          delete alert.acknowledged_by;
        } else {
          alert.acknowledged_by = acknowledgedBy;
        }
        if (acknowledgedAt === undefined) {
          delete alert.acknowledged_at;
        } else {
          alert.acknowledged_at = acknowledgedAt;
        }

        const workerId = alertWorkerId(alert);
        if (workerId && state.workers[workerId]) {
          state.workers[workerId].alert_status = workerAlertStatus(state.alerts, workerId);
        }
      }

      state.lastUpdate = Date.now();
    },
    
    resolveAlert: (state, action: PayloadAction<string>) => {
      const alertId = action.payload;
      const alert = state.alerts.find(a => a.id === alertId);
      
      if (alert) {
        alert.resolved = true;
        alert.resolved_at = Date.now();
        
        // Recompute from the worker's remaining active alerts.
        const workerId = alertWorkerId(alert);
        if (workerId && state.workers[workerId]) {
          state.workers[workerId].alert_status = workerAlertStatus(state.alerts, workerId);
        }
      }
      
      state.lastUpdate = Date.now();
    },
    
    clearAlerts: (state) => {
      state.alerts = [];
      
      // Reset all worker alert statuses
      Object.values(state.workers).forEach(worker => {
        worker.alert_status = 'normal';
      });
      
      state.lastUpdate = Date.now();
    },
    
    setConnectionStatus: (state, action: PayloadAction<WebSocketStatus>) => {
      state.connectionStatus = action.payload;
      state.lastUpdate = Date.now();
    },
    
    updateConnectionStatus: (state, action: PayloadAction<Partial<WebSocketStatus>>) => {
      state.connectionStatus = {
        ...state.connectionStatus,
        ...action.payload,
      };
      state.lastUpdate = Date.now();
    },
    
    resetRealtimeData: (state) => {
      state.workers = {};
      state.activities = {};
      state.alerts = [];
      state.openAlertSeed = null;
      state.activeZones = {};
      state.lastUpdate = Date.now();
    },
    
    batchUpdateWorkers: (state, action: PayloadAction<Worker[]>) => {
      // Clear existing zone counts
      state.activeZones = {};
      
      // Update all workers and recalculate zone counts
      action.payload.forEach(worker => {
        state.workers[worker.id] = worker;  // Use id instead of person_id
        
        if (worker.current_zone) {
          state.activeZones[worker.current_zone] = (state.activeZones[worker.current_zone] || 0) + 1;
        }
      });
      
      state.lastUpdate = Date.now();
    },
  },
});

export const {
  updateWorkerLocation,
  updateWorkerStatus,
  addWorker,
  removeWorker,
  addActivity,
  updateActivity,
  addAlert,
  seedAlerts,
  acknowledgeAlert,
  setAlertAcknowledgement,
  setAlertResolution,
  resolveAlert,
  clearAlerts,
  setConnectionStatus,
  updateConnectionStatus,
  resetRealtimeData,
  batchUpdateWorkers,
} = realtimeSlice.actions;

export default realtimeSlice.reducer;

// Selectors
const selectWorkersDict = (state: { realtime: RealtimeState }) => state.realtime.workers;
const selectAlertsList = (state: { realtime: RealtimeState }) => state.realtime.alerts;
const selectActivitiesDict = (state: { realtime: RealtimeState }) => state.realtime.activities;

export const selectActiveWorkers = createSelector(
  [selectWorkersDict],
  (workers) => Object.values(workers).filter(w => w.sensor_status === 'active'),
);

export const selectWorkerById = (workerId: string) => (state: { realtime: RealtimeState }) =>
  state.realtime.workers[workerId];

export const selectActiveAlerts = createSelector(
  [selectAlertsList],
  (alerts) => alerts.filter(a => !a.resolved),
);

export const selectUnacknowledgedAlerts = createSelector(
  [selectAlertsList],
  (alerts) => alerts.filter(a => !a.acknowledged && !a.resolved),
);

export const selectRecentActivities = createSelector(
  [selectActivitiesDict],
  (activities) => {
    const oneHourAgo = Date.now() - 60 * 60 * 1000;
    return Object.values(activities)
      .filter(a => a.start_time > oneHourAgo)
      .sort((a, b) => b.start_time - a.start_time);
  },
);

export const selectZoneOccupancy = (state: { realtime: RealtimeState }) =>
  state.realtime.activeZones;

export const selectConnectionStatus = (state: { realtime: RealtimeState }) =>
  state.realtime.connectionStatus;

// Raw alerts array (already a stable reference until the slice updates).
// Components that need a "latest N" view should select this and slice in
// `useMemo`, OR use createSelector themselves with a stable limit.
export const selectAllAlerts = selectAlertsList;
